import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getData, deleteData, postData, putData } from "../../../utility/Utility";
import Swal from "sweetalert2";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

function CompanyList() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const rowsPerPage = 10;

  const fetchCompanies = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const response = await postData("api/company/list", {
        search: search,
        page: page,
        limit: rowsPerPage
      });

      if (response?.success) {
        setCompanies(response.data.companies || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalItems(response.data.totalItems || 0);
      } else {
        toast.error(response?.message || "Failed to fetch companies");
      }
    } catch (error) {
      toast.error("Error fetching companies");
      console.error("Fetch companies error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies(currentPage + 1, searchTerm);
  }, [currentPage]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchCompanies(1, searchTerm);
  };

  const handleReset = () => {
    setSearchTerm("");
    setCurrentPage(0);
    fetchCompanies(1);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await postData(`api/company/delete`, {
          companyId: id
        });
        if (response.success) {
          toast.success(response.message || "Company deleted successfully");
          fetchCompanies(currentPage + 1, searchTerm);
        } else {
          toast.error(response.message || "Failed to delete company");
        }
      } catch (error) {
        toast.error("Error deleting company");
        console.error("Delete company error:", error);
      }
    }
  };

  const handleStatusChange = async (company) => {
    const newStatus = company.status === "active" ? "inactive" : "active";

    const result = await Swal.fire({
      title: `Are you sure you want to ${newStatus === "active" ? "activate" : "deactivate"} this company?`,
      text: `The company will be ${newStatus === "active" ? "activated" : "deactivated"}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${newStatus === "active" ? "activate" : "deactivate"} it!`,
    });

    if (result.isConfirmed) {
      try {
        const response = await postData(`api/company/update`, {
          status: newStatus,
          id: company?.id,
        });

        if (response.status) {
          toast.success(`Company ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
          fetchCompanies(currentPage + 1, searchTerm);
        } else {
          toast.error(response.message || `Failed to ${newStatus === "active" ? "activate" : "deactivate"} company`);
        }
      } catch (error) {
        toast.error(`Error ${newStatus === "active" ? "activating" : "deactivating"} company`);
        console.error("Status change error:", error);
      }
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">Company Management</h4>
              <p className="fw-semibold my-2">Company List</p>
            </div>
            <div>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/add-company")}
                disabled={loading}
              >
                {loading ? (
                  <CustomLoader loading={true} />
                ) : (
                  <i className="mdi mdi-plus-square me-1"></i>
                )}
                {loading ? "Adding..." : "Add Company"}
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-center">
                      <div className="col-md-12 col-lg-4">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control ps-4"
                            placeholder="Search companies..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <span className="input-group-text">
                            <i className="mdi mdi-magnify"></i>
                          </span>
                        </div>
                      </div>

                      <div className="col-md-12 col-lg-3 d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <CustomLoader loading={true} />
                          ) : null}
                          {loading ? "Searching..." : "Search"}
                        </button>

                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <CustomLoader loading={true} />
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Company Name</th>
                            <th>Description</th>
                            {/* <th>Status</th> */}
                            <th>Created At</th>
                            <th width="150px">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {companies.length > 0 ? (
                            companies.map((company, index) => (
                              <tr key={company._id}>
                                <td>{currentPage * rowsPerPage + index + 1}</td>
                                <td>{company.name}</td>
                                <td>{company.description}</td>
                                {/* <td>
                                  <span className={`badge text-capitalize bg-${company.status === 'active' ? 'success' : 'danger'}`}>
                                    {company.status}
                                  </span>
                                </td> */}
                                <td>
                                  {new Date(company.createdAt).toLocaleDateString()}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => navigate(`/update-company`, { state: { companyData: company } })}
                                    >
                                      <i className="mdi mdi-pencil"></i>
                                    </button>
                                    {/* <button
                                      className={`btn btn-sm btn-outline-${company.status === 'active' ? 'warning' : 'success'}`}
                                      onClick={() => handleStatusChange(company)}
                                      title={company.status === 'active' ? 'Deactivate' : 'Activate'}
                                    >
                                      <i className={`mdi mdi-${company.status === 'active' ? 'close' : 'check'}`}></i>
                                    </button> */}
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDelete(company._id)}
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center py-4">
                                No companies found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={<i className="mdi mdi-chevron-left"></i>}
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyList;